<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="900px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Tworzenie nowego użytkownika
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="username"
                                label="Nazwa użytkownika"
                                :error-messages="usernameErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="email"
                                label="E-mail"
                                outlined
                                dense
                                :error-messages="emailErrors"
                                required
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="firstName"
                                label="Imię"
                                outlined
                                dense
                                :error-messages="firstNameErrors"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="lastName"
                                label="Nazwisko"
                                outlined
                                dense
                                :error-messages="lastNameErrors"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="password"
                                label="Nowe hasło"
                                :type="showPassword ? 'text' : 'password'"
                                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                counter
                                hint="Ilość znaków 8-20, duża litera, cyfra oraz znak specjalny."
                                persistent-hint
                                :error-messages="passwordErrors"
                                autocomplete="new-password"
                                outlined
                                dense
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="confirmedNewPassword"
                                label="Powtórz hasło"
                                :type="showPasswordRepeat ? 'text' : 'password'"
                                :append-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
                                counter
                                outlined
                                dense
                                :error-messages="confirmedNewPasswordError"
                                autocomplete="new-password"
                                @click:append="showPasswordRepeat = !showPasswordRepeat"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="3" class="d-flex justify-end">
                            <v-checkbox
                                v-model="testAccount"
                                label="Konto testowe"
                                hide-details
                                class="mt-2"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" class="d-flex justify-end">
                            <v-checkbox
                                v-model="isCopyFromUser"
                                label="Kopiuj z innego użytkownika"
                                hide-details
                                class="mt-2"
                                @change="clearUserSearch"
                            />
                        </v-col>
                        <v-col cols="12" sm="5">
                            <v-autocomplete
                                v-model="copyFromUser"
                                :items="users"
                                :filter="customUsersFilter"
                                label="Wybierz użytkownika"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                :disabled="!isCopyFromUser"
                                :error-messages="copyFromUserErrors"
                                class="v-text-field--placeholder-dark"
                                dense
                                outlined
                                item-value="id"
                                item-text="firstName"
                                no-data-text="Nie znaleziono"
                                clearable
                                @click:clear="clearUserSearch"
                            >
                                <template #no-data>
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>użytkownika</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>

                                <template #selection="{item}">
                                    <template v-if="item.firstName && item.lastName">
                                        {{ item.firstName }} {{ item.lastName }} | {{ getDepartmentName(item) }}
                                    </template>
                                    <template v-else>
                                        {{ item.username }}
                                    </template>
                                </template>
                                <template #item="{item}">
                                    <v-list-item>
                                        <v-list-item-content>
                                            <template v-if="item.firstName && item.lastName">
                                                <v-list-item-title>{{ item.firstName }} {{ item.lastName }}</v-list-item-title>
                                            </template>
                                            <template v-else>
                                                <v-list-item-title>{{ item.username }}</v-list-item-title>
                                            </template>
                                            <v-list-item-subtitle>{{ getDepartmentName(item) }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider />
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading.saveUser"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading.saveUser"
                    @click="saveUser()"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import parse from '@/mixins/parse'

import { passwordRequirements } from '@/utils/customValidators'
import { errorHandler } from '@/utils/errorHandler'

import { required, email, sameAs } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'

let searchTimer

export default {
    name: 'CreateUserDialog',
    directives: { mask },
    mixins: [ customFilters, parse ],
    props: {
        isOpen: {
            type: Boolean
        },
    },
    data() {
        return {
            username: null,
            firstName: null,
            lastName: null,
            email: null,
            password: null,
            copyFromUser:null,
            isCopyFromUser: false,
            testAccount: false,
            confirmedNewPassword: null,
            actionLoading: {
                saveUser: false,
            },
            showPassword: false,
            showPasswordRepeat: false,
            productionSubDepartments: [],
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
        }
    },
    validations() {
        const validations = {
            username: {
                required
            },
            email: {
                email,
                required
            },
            firstName: {
                required
            },
            lastName: {
                required
            },
            password: {
                passwordRequirements
            },
            confirmedNewPassword: {
                sameAsNewPassword: sameAs('password')
            }
        }

        if (this.password) {
            validations['password'] = {
                passwordRequirements
            }
            validations['confirmedNewPassword'] = {
                sameAsNewPassword: sameAs('password')
            }
        }

        if (this.isCopyFromUser) {
            validations['copyFromUser'] = {
                required
            }
        }

        return validations
    },
    computed: {
        usernameErrors() {
            const errors = []

            if (!this.$v.username.$dirty) return errors
            !this.$v.username.required && errors.push('Nazwa użytkownika jest wymagana.')

            return errors
        },
        emailErrors() {
            const errors = []

            if (!this.$v.email.$dirty) return errors
            !this.$v.email.required && errors.push('Adres e-mail jest wymagany.')
            !this.$v.email.email && errors.push('Proszę podać poprawny adres e-mail.')

            return errors
        },
        firstNameErrors() {
            const errors = []

            if (!this.$v.firstName.$dirty) return errors
            !this.$v.firstName.required && errors.push('Imię jest wymagane.')

            return errors
        },
        lastNameErrors() {
            const errors = []

            if (!this.$v.lastName.$dirty) return errors
            !this.$v.lastName.required && errors.push('Nazwisko jest wymagane.')

            return errors
        },
        passwordErrors() {
            const errors = []

            if (!this.$v.password.$dirty) return errors
            !this.$v.password.passwordRequirements && errors.push('Hasło musi zawierać od 8 do 20 znaków, wielką literę, cyfrę oraz znak specjalny.')

            return errors
        },
        confirmedNewPasswordError() {
            const errors = []

            if (!this.$v.confirmedNewPassword.$dirty) return errors
            !this.$v.confirmedNewPassword.sameAsNewPassword && errors.push('Hasła nie są identyczne.')

            return errors
        },
        copyFromUserErrors() {
            const errors = []

            if (!this.$v.copyFromUser?.$dirty) return errors
            !this.$v.copyFromUser.required && errors.push('Proszę wybrać użytkownika do skopiowania.')

            return errors
        }
    },
    watch: {
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        clearUserSearch() {
            this.usersSearch = null
            this.copyFromUser = null
            this.usersSearchLoading = false
        },
        getDepartmentName(user) {
            const department = user.profile?.productionDepartment?.name

            return department ?? ''
        },
        saveUser() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            const payload = {
                username: this.username,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                password: this.password,
                profile: {
                    testAccount: this.testAccount
                },
                copyFromUserId: this.isCopyFromUser ? this.copyFromUser : null
            }

            if (!this.password) {
                delete payload['password']
            }

            this.actionLoading.saveUser = true

            Promise.all([
                this.$store.dispatch('administration/getCostCenterNumbers'),
                this.$store.dispatch('administration/addUser', { payload })
            ])
                .then(([, newUserResponse]) => {
                    this.$emit('user-created', newUserResponse.data)
                    this.$emit('on-close')

                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveUser = false
                    this.onClose()
                })
        },
        searchUsers(query = '') {
            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        onClose() {
            setTimeout(() => {
                this.username = null
                this.firstName = null
                this.lastName = null,
                this.email = null,
                this.password = null,
                this.confirmedNewPassword = null,
                this.showPassword = false,
                this.showPasswordRepeat = false
                this.copyFromUser = null
                this.isCopyFromUser = false
                this.testAccount = false
                this.usersSearch = null
                this.usersSearchLoading = false
                this.users = []
            }, 500)
            this.$v.$reset()

            this.$emit('on-close')
        }
    }
}
</script>
